var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Row, Col, Button } from "@folio/stripes/components";
import CustomAccordionSet from "../../../components/CustomAccordionSet";
import { RoleEnum } from "../../../types/RoleEnum";
import { ExtendedInformationAccordion } from "../../../components/ExtendedInformationAccordion";
import { PublisherPublicCatalogLink } from "../../../components/PublisherPublicCatalogLink";
import { GeneralSection } from "./GeneralSection";
import { AlternativeNamesSection } from "./AlternativeNamesSection";
import { AddressSection } from "./AddressSection";
import { ContactsSection } from "./ContactsSection";
import { GuarantorsSection } from "./GuarantorsSection";
import { CommentsSection } from "./CommentsSection";
import { TimestampsSection } from "./TimestampsSection";
import { WorkSpecSection } from "./WorkSpecSection";
import { RegistrationsSection } from "./RegistrationsSection";
import { Kult1801ReportSection } from "./Kult1801ReportSection";
import { hasActiveOneOffRegistration } from "../../../utils/registrationUtils";
var mergePublisherData = function (publisher, data) { return (__assign(__assign(__assign({}, publisher), data), { mailingAddress: __assign(__assign({}, publisher === null || publisher === void 0 ? void 0 : publisher.mailingAddress), data === null || data === void 0 ? void 0 : data.mailingAddress), mainAddress: __assign(__assign({}, publisher.mainAddress), ((data === null || data === void 0 ? void 0 : data.mainAddress) || {})), contacts: (data === null || data === void 0 ? void 0 : data.contacts) !== undefined ? data.contacts : publisher.contacts, guarantors: (data === null || data === void 0 ? void 0 : data.guarantors) !== undefined ? data.guarantors : publisher.guarantors, alternativeNames: (data === null || data === void 0 ? void 0 : data.alternativeNames) !== undefined
        ? data.alternativeNames
        : publisher.alternativeNames })); };
var PublisherDetail = function (_a) {
    var _b, _c, _d;
    var publisher = _a.publisher, data = _a.data, showKult1801Report = _a.showKult1801Report, _e = _a.ownPublisher, ownPublisher = _e === void 0 ? false : _e, onRegistrationConverted = _a.onRegistrationConverted;
    var _f = useState(true), areAccordionsExpanded = _f[0], setAreAccordionsExpanded = _f[1];
    var intl = useIntl();
    var formatMessage = intl.formatMessage;
    var mergedPublisher = useMemo(function () { return mergePublisherData(publisher, data); }, [publisher, data]);
    var hasOneOffRegistration = hasActiveOneOffRegistration(mergedPublisher);
    var workSpecification = mergedPublisher.workSpecification
        ? mergedPublisher.workSpecification
        : undefined;
    var hasMailingAddress = !!(((_b = mergedPublisher.mailingAddress) === null || _b === void 0 ? void 0 : _b.street) ||
        ((_c = mergedPublisher.mailingAddress) === null || _c === void 0 ? void 0 : _c.postalCode) ||
        ((_d = mergedPublisher.mailingAddress) === null || _d === void 0 ? void 0 : _d.municipality));
    var accordionSections = useMemo(function () {
        var _a, _b, _c;
        var sections = [
            {
                id: "general",
                label: intl.formatMessage({
                    id: "ui-br-publishers.publishers.accordion.label.general",
                }),
                content: React.createElement(GeneralSection, { publisher: mergedPublisher }),
            },
            {
                id: "registrations",
                label: intl.formatMessage({
                    id: "ui-br-publishers.publishers.accordion.label.registrations",
                }),
                content: (React.createElement(RegistrationsSection, { publisher: mergedPublisher, ownPublisher: ownPublisher, onConvertSuccess: onRegistrationConverted })),
            },
            {
                id: "alternativeNames",
                label: intl.formatMessage({
                    id: "ui-br-publishers.publishers.accordion.label.alternativeNames",
                }),
                content: (React.createElement(AlternativeNamesSection, { alternativeNames: mergedPublisher.alternativeNames })),
            },
            {
                id: "address",
                label: "Adresa",
                content: (React.createElement(AddressSection, { mainAddress: mergedPublisher.mainAddress, mailingAddress: mergedPublisher.mailingAddress, hasMailingAddress: hasMailingAddress, mainAddressPublishConsent: mergedPublisher.mainAddressPublishConsent, mailingAddressPublishConsent: mergedPublisher.mailingAddressPublishConsent, displayAddressPublishConsent: mergedPublisher.legalForm === "NATURAL" })),
            },
            {
                id: "contacts",
                label: intl.formatMessage({
                    id: "ui-br-publishers.publishers.accordion.label.contacts",
                }),
                content: React.createElement(ContactsSection, { contacts: mergedPublisher.contacts }),
            },
            {
                id: "guarantors",
                label: intl.formatMessage({
                    id: "ui-br-publishers.publishers.accordion.label.guarantors",
                }),
                content: React.createElement(GuarantorsSection, { guarantors: mergedPublisher.guarantors }),
            },
            {
                id: "comments",
                label: intl.formatMessage({
                    id: "ui-br-publishers.publishers.accordion.label.comments",
                }),
                requiredRole: RoleEnum.AGENCY_WORKER,
                content: (React.createElement(CommentsSection, { internalComment: mergedPublisher.internalComment })),
            },
            {
                id: "timestamps",
                label: formatMessage({
                    id: "ui-br-publishers.publishers.accordion.label.timestamps",
                }),
                content: React.createElement(TimestampsSection, { publisher: mergedPublisher }),
            },
        ];
        if (hasOneOffRegistration) {
            sections.push({
                id: "workSpec",
                label: formatMessage({
                    id: "stripes-core.publishers.accordion.label.workSpec",
                }),
                content: React.createElement(WorkSpecSection, { workSpec: workSpecification }),
            });
        }
        if (showKult1801Report) {
            var publisherId = (_a = data === null || data === void 0 ? void 0 : data.id) !== null && _a !== void 0 ? _a : publisher.id;
            if (publisherId) {
                sections.push({
                    id: "kult1801Report",
                    label: formatMessage({
                        id: "ui-br-publishers.accordion.label.kult1801Report",
                    }),
                    content: React.createElement(Kult1801ReportSection, { publisherId: publisherId }),
                });
            }
        }
        sections.push({
            id: "extendedInformation",
            label: formatMessage({
                id: "ui-br-publishers.accordions.label.extendedInformation",
            }),
            content: (React.createElement(ExtendedInformationAccordion, { id: (_b = data === null || data === void 0 ? void 0 : data.id) !== null && _b !== void 0 ? _b : publisher.id, publicCatalogLink: React.createElement(PublisherPublicCatalogLink, { publisherId: (_c = data === null || data === void 0 ? void 0 : data.id) !== null && _c !== void 0 ? _c : publisher.id }) })),
        });
        return sections;
    }, [
        intl,
        mergedPublisher,
        hasMailingAddress,
        hasOneOffRegistration,
        formatMessage,
        data === null || data === void 0 ? void 0 : data.id,
        publisher.id,
        workSpecification,
        showKult1801Report,
        ownPublisher,
        onRegistrationConverted,
    ]);
    var handleToggleAllAccordions = function (expanded) {
        setAreAccordionsExpanded(expanded);
    };
    var toggleAccordionsExpansion = function () {
        setAreAccordionsExpanded(function (prev) { return !prev; });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("h1", null,
            mergedPublisher.name,
            mergedPublisher.nameSuffix ? ", ".concat(mergedPublisher.nameSuffix) : ""),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12, style: { display: "flex", justifyContent: "flex-end" } },
                React.createElement(Button, { onClick: toggleAccordionsExpansion, buttonStyle: "default" }, areAccordionsExpanded ? (React.createElement(FormattedMessage, { id: "ui-br-publishers.button.collapse.all" })) : (React.createElement(FormattedMessage, { id: "ui-br-publishers.button.expand.all" }))))),
        React.createElement(CustomAccordionSet, { accordions: accordionSections, expandAll: areAccordionsExpanded, onToggleExpandAll: handleToggleAllAccordions })));
};
export default PublisherDetail;
